import React from 'react';

import {
  DescriptionList,
  DescriptionItem,
  Stack,
  Typography,
  Help,
  Link,
} from '@gogame/components';

import { Schedule } from 'scenes/SmartRules/components/Schedule';

const DelayAfterActionTaken = ({ days }) => (
  <Stack spacing={0.5} direction={'row'} alignItems={'center'}>
    <Typography>{`${days} days resting period before next action`}</Typography>
    <Help>
      The amount of the days after conditions met that will be skipped before
      next run
    </Help>
  </Stack>
);

const EntityRestingPeriod = ({ days }) => (
  <Stack spacing={0.5} direction={'row'} alignItems={'center'}>
    <Typography>{`${days} days of cross-rule resting period`}</Typography>
    <Help>
      Put ‘on hold’ all the modified entities, so that other rules can’t change
      them.
    </Help>
  </Stack>
);

function getModifyActionValue(action) {
  return action?.limit
    ? `${action.actionType} ${action.operator} ${action.value} (bottom limit $ ${action.limit})`
    : action?.operator
      ? `${action.actionType} ${action.operator} ${action.value}`
      : action?.actionType;
}
const General = ({ data, onOpenInfluencerCampaigns, onOpenMasterCampaign }) => (
  <DescriptionList disableGutters data={data}>
    <DescriptionItem
      label={'Type'}
      field={'action.skip'}
      placeholder={'Notifying'}
      valueParser={(skip) => (skip ? 'Auto-modifying' : 'Modifying')}
    />
    <DescriptionItem
      label={'Modify action'}
      field={'action'}
      valueParser={getModifyActionValue}
    />
    <DescriptionItem label={'Affected level'} field={'target.dimension.name'} />
    <DescriptionItem
      label={'Secondary affected level'}
      field={'target.secondaryDimension.name'}
    />
    <DescriptionItem
      label={'Networks'}
      type={'typographyList'}
      limit={1}
      field={'target.networks'}
      unitsLabel={'networks'}
    />
    <DescriptionItem
      label={'Applications'}
      type={'typographyList'}
      limit={1}
      field={'target.applications'}
      unitsLabel={'applications'}
    />
    <DescriptionItem
      label={'Platforms'}
      type={'typographyList'}
      limit={1}
      field={'target.platforms'}
      unitsLabel={'platforms'}
    />
    <DescriptionItem
      label={'Сhannels'}
      type={'typographyList'}
      limit={1}
      field={'target.channels'}
      unitsLabel={'channels'}
    />
    <DescriptionItem
      label={'Affiliates'}
      type={'typographyList'}
      limit={1}
      field={'target.affiliates'}
      unitsLabel={'affiliates'}
    />
    <DescriptionItem
      label={'Countries'}
      type={'typographyList'}
      limit={1}
      field={'target.countries'}
      unitsLabel={'countries'}
    />
    <DescriptionItem
      label={'Traffic Type'}
      field={'target.trafficType.name'}
    />
    <DescriptionItem
      label={'Labels'}
      type={'typographyList'}
      limit={1}
      field={'target.labels'}
      unitsLabel={'labels'}
    />
    <DescriptionItem
      label={'Campaigns'}
      type={'typographyList'}
      limit={1}
      field={'target.campaigns'}
      unitsLabel={'campaigns'}
    />
    <DescriptionItem
      label={'Partner campaigns'}
      type={'typographyList'}
      limit={1}
      field={'target.partnerCampaigns'}
      unitsLabel={'partner campaigns'}
    />
    <DescriptionItem
      label={'AdSets'}
      type={'typographyList'}
      limit={1}
      field={'target.adSets'}
      unitsLabel={'adsets'}
    />
    <DescriptionItem
      label={'Master campaigns'}
      type={'typographyList'}
      limit={1}
      field={'target.masterCampaigns'}
      unitsLabel={'Master campaigns'}
      listType={'link'}
      item={({ id, name }) => (
        <Link onClick={() => onOpenMasterCampaign(id)}>
          <Typography>{name || id}</Typography>
        </Link>
      )}
    />
    <DescriptionItem
      label={'Social Media Platforms'}
      type={'typographyList'}
      limit={1}
      field={'target.socialMediaPlatforms'}
      unitsLabel={'Social Media Platforms'}
      item={({ id, name }) => <Typography>{name || id}</Typography>}
    />
    <DescriptionItem
      label={'Influencer Campaigns'}
      type={'typographyList'}
      limit={1}
      field={'target.influencerCampaigns'}
      unitsLabel={'Influencer Campaigns'}
      listType={'link'}
      item={({ id, name }) => (
        <Link onClick={() => onOpenInfluencerCampaigns(id)}>
          <Typography>{name || id}</Typography>
        </Link>
      )}
    />
    <DescriptionItem
      label={'Campaign owners'}
      type={'typographyList'}
      limit={1}
      field={'target.owners'}
      unitsLabel={'owners'}
    />
    <DescriptionItem label={'Pay to'} field={'payTo.name'} />
    <DescriptionItem
      label={'Concept added'}
      field={'creativesTarget.conceptCreationPeriod.name'}
    />
    <DescriptionItem
      label={'Creative added'}
      field={'creativesTarget.creativeCreationPeriod.name'}
    />

    <DescriptionItem
      label={'Creative types'}
      type={'typographyList'}
      limit={1}
      field={'creativesTarget.creativeTypes'}
      unitsLabel={'creative types'}
    />

    <DescriptionItem
      label={'Media Types'}
      type={'typographyList'}
      limit={1}
      field={'creativesTarget.mediaTypes'}
      unitsLabel={'media types'}
    />

    <DescriptionItem
      label={'Languages'}
      type={'typographyList'}
      limit={1}
      field={'creativesTarget.languages'}
      unitsLabel={'languages'}
    />
    <DescriptionItem
      label={'Orientations'}
      type={'typographyList'}
      limit={1}
      field={'creativesTarget.orientations'}
      unitsLabel={'orientations'}
    />
    <DescriptionItem
      label={'Statuses'}
      type={'typographyList'}
      limit={1}
      field={'creativesTarget.statuses'}
      unitsLabel={'statuses'}
    />

    <DescriptionItem label={'Data period'} field={'trackerPeriod.name'} />
    <DescriptionItem
      label={'Compare to period'}
      field={'trackerPeriodToCompare.name'}
    />
    <DescriptionItem label={'Period type'} field={'periodType.name'} />
    <DescriptionItem
      label={'Extra metrics'}
      type={'typographyList'}
      limit={1}
      field={'extraMetrics'}
      unitsLabel={'extra metrics'}
    />

    <DescriptionItem
      label={'Email notifications'}
      type={'typographyList'}
      limit={4}
      field={'notifications'}
      unitsLabel={'users'}
    />
    <DescriptionItem
      label={'Schedule'}
      field={'schedule'}
      type={(schedule) => {
        return (
          <Stack spacing={1}>
            <Typography>
              <Schedule data={data} />
            </Typography>
            {schedule?.delayAfterActionTaken !== undefined && (
              <DelayAfterActionTaken
                days={data.schedule.delayAfterActionTaken}
              />
            )}

            {data?.action?.entityRestingPeriod !== undefined && (
              <EntityRestingPeriod days={data.schedule.entityRestingPeriod} />
            )}
          </Stack>
        );
      }}
    />
  </DescriptionList>
);

export default General;
